import moment from 'moment';
import type Moment from 'moment';

import {
    DATE_RANGES_PAST,
    DATE_RANGES_FUTURE,
    LAST_365_DAYS,
    DATE_RANGES_MINUTES,
    DATE_RANGES_FORMS,
    DATE_RANGES_ANALYTICS,
    DATE_RANGES_ATTRIBUTION,
    DATE_RANGES_DASHBOARD,
    DATE_RANGES_ENGAGEMENT_DASHBOARD,
} from 'nutshell-core/constants';

import {CalendarPenRegularReportIcon} from 'shells/icon/svg/calendar-pen-regular-report-icon';
import {CalendarRegularReportIcon} from 'shells/icon/svg/calendar-regular-report-icon';
import {CancelRegularReportIcon} from 'shells/icon/svg/cancel-regular-report-icon';
import {ChartLineRegularReportIcon} from 'shells/icon/svg/chart-line-regular-report-icon';
import {ChatRegularReportIcon} from 'shells/icon/svg/chat-regular-report-icon';
import {ChatbotRegularReportIcon} from 'shells/icon/svg/chatbot-regular-report-icon';
import {CheckRegularReportIcon} from 'shells/icon/svg/check-regular-report-icon';
import {ChevronRegularRightLeadReportIcon} from 'shells/icon/svg/chevron-regular-right-lead-report-icon';
import {CogRegularReportIcon} from 'shells/icon/svg/cog-regular-report-icon';
import {EnvelopeRegularReportIcon} from 'shells/icon/svg/envelope-regular-report-icon';
import {FilterRegularReportIcon} from 'shells/icon/svg/filter-regular-report-icon';
import {ProductReportIcon} from 'shells/icon/svg/product-report-icon';
import {StarRegularReportIcon} from 'shells/icon/svg/star-regular-report-icon';
import {TagRegularDiagonalReportIcon} from 'shells/icon/svg/tag-regular-diagonal-report-icon';

import type {SavedFiltersReportType} from 'nutshell-graphql-types';

import {DEFAULT_CHATBOT_REPORT_PERIOD} from './chatbot/consts';
import {getAttributionReportDescription} from './report-helpers';
import type {
    ReportKey,
    LeadsReportKey,
    FunnelReportKey,
    ReportSavedTypeKey,
    ExportKey,
    ReportSegment,
    ReportFacet,
} from './report-types';
import {
    LOSSES,
    LOSSES_SAVED_TYPE,
    NEW_LEADS,
    NEW_LEADS_SAVED_TYPE,
    SALES,
    SALES_SAVED_TYPE,
    PRODUCTS,
    PRODUCTS_SAVED_TYPE,
    FORECAST,
    FORECAST_SAVED_TYPE,
    FUNNEL,
    FUNNEL_SAVED_TYPE,
    CUSTOM,
    CUSTOM_SAVED_TYPE,
    EMAIL,
    TASKS_SAVED_TYPE,
    EMAIL_SAVED_TYPE,
    ACTIVITY,
    ACTIVITY_SAVED_TYPE,
    AUDIENCE_MEMBERSHIP,
    EDITION_ENGAGEMENT,
    SMS_EDITION_ENGAGEMENT,
    DRIP_EDITION_ENGAGEMENT,
    SMS_DRIP_EDITION_ENGAGEMENT,
    FORM_ENGAGEMENT,
    LEAD_LIST_EXPORT_KEY,
    REPORT_DATA_EXPORT_KEY,
    REPORT_CHART_EXPORT_KEY,
    REPORT_ACTIVITY_DETAILS_EXPORT_KEY,
    REPORT_ACTIVITY_OVERVIEW_EXPORT_KEY,
    REPORT_FUNNEL_EXPORT_KEY,
    REPORT_PRODUCTS_EXPORT_KEY,
    SITE_ANALYTICS,
    ATTRIBUTION,
    ATTRIBUTION_SAVED_TYPE,
    MCFXCOMPANIES,
    SCHEDULER,
    COMPETITORS,
    DASHBOARD,
    ENGAGEMENT_DASHBOARD,
    LANDING_PAGES,
    TASKS,
    ENGAGEMENT,
    ENGAGEMENT_SAVED_TYPE,
    QUOTES,
    INVOICES,
    INSTALLMENTS,
    ENGAGEMENT_LEADS,
    ENGAGEMENT_SALES,
    WIDGET_OPENS,
    COMPLETED_ACTIONS,
    VISITORS,
    ENGAGEMENT_BAR_LEADS,
    CHATBOT,
    SCHEDULER_REPORT,
} from './report-types';

export {
    LOSSES,
    NEW_LEADS,
    SALES,
    PRODUCTS,
    FORECAST,
    ATTRIBUTION,
    FUNNEL,
    CUSTOM,
    ACTIVITY,
    EMAIL,
    SITE_ANALYTICS,
    LEAD_LIST_EXPORT_KEY,
    REPORT_DATA_EXPORT_KEY,
    REPORT_CHART_EXPORT_KEY,
    REPORT_ACTIVITY_DETAILS_EXPORT_KEY,
    REPORT_ACTIVITY_OVERVIEW_EXPORT_KEY,
    REPORT_FUNNEL_EXPORT_KEY,
    REPORT_PRODUCTS_EXPORT_KEY,
    AUDIENCE_MEMBERSHIP,
    EDITION_ENGAGEMENT,
    SMS_EDITION_ENGAGEMENT,
    DRIP_EDITION_ENGAGEMENT,
    SMS_DRIP_EDITION_ENGAGEMENT,
    FORM_ENGAGEMENT,
    MCFXCOMPANIES,
    SCHEDULER,
    LANDING_PAGES,
    TASKS,
    ENGAGEMENT,
    QUOTES,
    INVOICES,
    INSTALLMENTS,
    ENGAGEMENT_LEADS,
    ENGAGEMENT_BAR_LEADS,
    ENGAGEMENT_SALES,
    WIDGET_OPENS,
    COMPLETED_ACTIONS,
    VISITORS,
    CHATBOT,
    SCHEDULER_REPORT,
};
export const NO_SEGMENT_ID = 'none';
export const SEGMENT_PRIORITY_FACET = 'count';

// Used in case of a URL with no facet, which breaks some reports pages
export const FALLBACK_FACET: ReportFacet = 'total';

// We reference this ID in multiple places for onboarding,
// downloading a browser-generated image of this div, etc.
export const CHART_HERO_DIV_ID = 'chart-hero';

type SegmentOption = {
    id: ReportSegment | 'none';
    label: string;
};

// Segment-by options
export const SEGMENT_NONE = {id: NO_SEGMENT_ID, label: 'None'} as const;
export const SEGMENT_SUBSCRIPTION_STATUS = {
    id: 'subscriptionStatus',
    label: 'Subscription status',
    shouldStack: {count: true, total: false},
} as const;
export const SEGMENT_INTERACTION_TYPE = {
    id: 'interactionType',
    label: 'Interaction',
    shouldStack: {count: false, total: false},
} as const;
export const SEGMENT_ACTION_TYPE = {
    id: 'actionType',
    label: 'Action type',
    shouldStack: {count: true, total: false},
} as const;
export const SEGMENT_PRODUCT = {
    id: 'products',
    label: 'Product',
    shouldStack: {count: false, total: true},
} as const;
export const SEGMENT_PRODUCT_CATEGORY = {
    id: 'productCategories',
    label: 'Product category',
    shouldStack: {count: false, total: true},
} as const;
export const SEGMENT_SOURCE = {
    id: 'sources',
    label: 'Source',
    shouldStack: {count: false, total: false},
} as const;

export const SEGMENT_CHANNEL = {
    id: 'channels',
    label: 'Channel',
    shouldStack: {count: false, total: false},
} as const;
export const SEGMENT_COMPETITOR = {
    id: 'competitors',
    label: 'Competitor',
    shouldStack: {count: false, total: false},
} as const;
export const SEGMENT_ASSIGNEE = {
    id: 'assignees',
    label: 'Assignee',
    shouldStack: {count: true, total: true},
} as const;
export const SEGMENT_USER = {
    id: 'user',
    label: 'User',
    shouldStack: {count: false, total: false},
} as const;
export const SEGMENT_STAGE = {
    id: 'stages',
    label: 'Stage',
    shouldStack: {count: true, total: true},
} as const;
export const SEGMENT_STATUS = {
    id: 'status',
    label: 'Status',
    shouldStack: {count: true, total: true},
} as const;
export const SEGMENT_TAG = {
    id: 'tags',
    label: 'Tag',
    shouldStack: {count: false, total: false},
} as const;
export const SEGMENT_OUTCOME = {
    id: 'outcomes',
    label: 'Outcome',
    shouldStack: {count: true, total: true},
} as const;
export const SEGMENT_TERRITORY = {
    id: 'territories',
    label: 'Territory',
    shouldStack: {count: true, total: true},
} as const;
export const SEGMENT_ACTIVITY_TYPE = {
    id: 'activityType',
    label: 'Type',
    shouldStack: {count: true, total: true},
} as const;
export const SEGMENT_EMAIL_SEQUENCE_TEMPLATE_GROUP = {
    id: 'emailSequenceTemplateGroups',
    label: 'Template group',
    shouldStack: {count: true, total: true},
} as const;
export const SEGMENT_LEAD_STATUS = {
    id: 'leadStatus',
    label: 'Outcome',
    shouldStack: {count: true, total: true},
} as const;
export const SEGMENT_TASK_STATUS = {
    id: 'taskStatuses',
    label: 'Status',
    shouldStack: {count: true, total: true},
} as const;
export const SEGMENT_QUOTE_STATUS = {
    id: 'salesDocumentStatuses',
    label: 'Status',
    shouldStack: {count: false, total: false},
} as const;
export const SEGMENT_INVOICE_STATUS = {
    id: 'salesDocumentStatuses',
    label: 'Status',
    shouldStack: {count: false, total: false},
} as const;

export const SEGMENT_INSTALLMENT_SERIES = {
    id: 'installmentSeries',
    label: 'Installment buckets',
    shouldStack: {count: true, total: true},
} as const;

export const SEGMENTS = [
    SEGMENT_SUBSCRIPTION_STATUS,
    SEGMENT_INTERACTION_TYPE,
    SEGMENT_ACTION_TYPE,
    SEGMENT_PRODUCT,
    SEGMENT_PRODUCT_CATEGORY,
    SEGMENT_SOURCE,
    SEGMENT_COMPETITOR,
    SEGMENT_ASSIGNEE,
    SEGMENT_USER,
    SEGMENT_STAGE,
    SEGMENT_STATUS,
    SEGMENT_TAG,
    SEGMENT_OUTCOME,
    SEGMENT_TERRITORY,
    SEGMENT_ACTIVITY_TYPE,
    SEGMENT_EMAIL_SEQUENCE_TEMPLATE_GROUP,
    SEGMENT_LEAD_STATUS,
    SEGMENT_CHANNEL,
    SEGMENT_TASK_STATUS,
    SEGMENT_QUOTE_STATUS,
    SEGMENT_INSTALLMENT_SERIES,
] as const;

/* Temporary activity report schema as it uses a couple fields that aren't
in the default leads schema we're using for other reports */
export const ACTIVITY_REPORT_SCHEMA = {
    participant: {
        name: 'participant',
        type: 'relationship',
        title: 'Users & teams',
        description: 'The participants on an activity',
        isFilterable: true,
        relationships: ['users', 'teams'],
        isSortable: false,
        isDisplayable: false,
    },
    activityType: {
        name: 'activityType',
        type: 'relationship',
        title: 'Activity type',
        description: 'The activity type of the activity',
        isFilterable: true,
        relationships: ['activityTypes'],
        isSortable: false,
        isDisplayable: false,
    },
} as const;

export const TASK_REPORT_SCHEMA = {
    assignee: {
        name: 'assignee',
        type: 'relationship',
        title: 'Assignee',
        description: 'The user assigned to the task',
        isFilterable: true,
        relationships: ['users'],
        isSortable: false,
        isDisplayable: false,
        isAndable: false,
        isSingleSelect: true,
    },
    stageset: {
        name: 'stageset',
        type: 'relationship',
        title: 'Pipeline',
        description: 'The pipeline associated with the task',
        isFilterable: true,
        relationships: ['stagesets'],
        isSortable: false,
        isDisplayable: false,
        isAndable: false,
        isSingleSelect: true,
        unclearable: true,
    },
} as const;

/* Temporary email report schema as it uses a couple fields that aren't
in the default leads schema we're using for other reports */
export const EMAIL_REPORT_SCHEMA = {
    recipient: {
        name: 'recipient',
        type: 'relationship',
        title: 'Users & teams',
        description: 'The recipients or senders on this email',
        isFilterable: true,
        relationships: ['users', 'teams'],
        isSortable: false,
        isDisplayable: false,
    },
    emailSequenceTemplateGroup: {
        name: 'emailSequenceTemplateGroups',
        type: 'relationship',
        title: 'Email template group',
        description: 'The template group this email was sent from',
        isFilterable: true,
        nullOptionText: 'Uncategorized',
        relationships: ['emailSequenceTemplateGroups'],
        isSortable: false,
        isDisplayable: false,
    },
} as const;

const DEFAULT_FIELDS_LOSSES =
    'name,createdTime,value,owner,milestone,confidence,closedTime,sources';
const DEFAULT_FIELDS_NEW_LEADS =
    'name,createdTime,value,owner,milestone,confidence,closedTime,sources';
const DEFAULT_FIELDS_SALES = 'name,createdTime,value,owner,milestone,confidence,closedTime,sources';
const DEFAULT_FIELDS_CUSTOM =
    'name,createdTime,value,owner,milestone,confidence,closedTime,sources';
const DEFAULT_FIELDS_FORECAST =
    'name,createdTime,value,owner,milestone,confidence,closedTime,sources';
// TODO update this
const DEFAULT_FIELDS_ATTRIBUTION =
    'name,createdTime,value,owner,milestone,confidence,closedTime,sources';
const DEFAULT_FIELDS_AUDIENCE_MEMBERSHIP =
    'name,email,phone,emailOpenRate,emailClickRate,marketingStatus,smsStatus,engagementRating,tags';
const DEFAULT_FIELDS_EDITION_ENGAGEMENT = 'name,email,phone,engagementRating,tags,marketingStatus';
const DEFAULT_FIELDS_SMS_EDITION_ENGAGEMENT = 'name,email,phone,smsStatus,tags';
const DEFAULT_FIELDS_DRIP_EDITION_ENGAGEMENT =
    'name,email,phone,engagementRating,tags,marketingStatus';
const DEFAULT_FIELDS_SMS_DRIP_EDITION_ENGAGEMENT = 'name,email,phone,smsStatus,tags';
const DEFAULT_FIELDS_FORM_ENGAGEMENT = 'name,createdTime';
export const DEFAULT_FIELDS_MCFXCOMPANIES =
    'name,matchedContacts,createdTime,region,mcfxIndustry,numberOfVisits,numberOfEmployees,revenue,phone,website,address,convertedTime';
export const DEFAULT_FIELDS_ENGAGEMENT_BAR_LEADS =
    'name,creatorOriginCategory,createdTime,value,owner,stageset,milestone,closedTime,tags,sources';
export const DEFAULT_SORT_FIELD_ENGAGEMENT_BAR_LEADS = '-createdTime';

// Chart bucket names
export const OTHER_BUCKET_NAME = 'Other';
export const NONE_BUCKET_NAME = 'None';

// Chart gap options
export const GAP_SELECT_VALUES_DAY_TO_YEAR = [
    {id: 'd', label: 'Day'},
    {id: 'w', label: 'Week'},
    {id: 'm', label: 'Month'},
    {id: 'q', label: 'Quarter'},
    {id: 'y', label: 'Year'},
] as const;

export const GAP_SELECT_INDEX_DAY = 0;

// Chart gap options
export const EDITION_GAP_SELECT_VALUES = [
    {id: 'i', label: 'Minute'},
    {id: 'h', label: 'Hour'},
    {id: 'd', label: 'Day'},
] as const;

export const GAP_SELECT_VALUES_DAY_TO_MONTH = [
    {id: 'd', label: 'Day'},
    {id: 'w', label: 'Week'},
    {id: 'm', label: 'Month'},
] as const;

export const EDITION_GAP_INITIAL_VALUES = [
    {period: '+h1', gap: 'i'},
    {period: '+h4', gap: 'i'},
    {period: '+h12', gap: 'i'},
    {period: '+d1', gap: 'h'},
    {period: '+d2', gap: 'h'},
    {period: '+d7', gap: 'h'},
] as const;

export const DAY_TO_MONTH_REPORT_KEYS = [
    'siteAnalytics',
    'widgetOpens',
    'visitors',
    'completedActions',
    'engagementLeads',
    'engagementSales',
] as const;

const DATE_RANGES_PAST_WITHOUT_TODAY = DATE_RANGES_PAST.filter((dateRange) => dateRange.id !== 'd');

const leadsReportConstants = {
    [LOSSES]: {
        title: 'Losses',
        description: 'Why are we losing business?',
        icon: CancelRegularReportIcon,
        documentTitle: 'Losses report · Nutshell',
        baseUrl: '/reports/losses',
        defaultFields: DEFAULT_FIELDS_LOSSES,
        defaultState: {
            search: `?report%5BdateRangeType%5D=closedTime&report%5Bgap%5D=w&report%5Bfacet%5D=total&filter%5BclosedTime%5D=-d365&filter%5Bstatus%5D=Lost%2CCancelled&list%5Bfields%5D%5Bleads%5D=${encodeURIComponent(
                DEFAULT_FIELDS_LOSSES
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
            dateRangeType: 'closedTime',
            dateRangeTypeFilter: LAST_365_DAYS.id,
        },
        dateRanges: DATE_RANGES_PAST,
        segmentOptions: [
            SEGMENT_NONE,
            SEGMENT_OUTCOME,
            SEGMENT_COMPETITOR,
            SEGMENT_PRODUCT,
            SEGMENT_PRODUCT_CATEGORY,
            SEGMENT_ASSIGNEE,
            SEGMENT_TERRITORY,
            SEGMENT_SOURCE,
            SEGMENT_TAG,
            SEGMENT_CHANNEL,
        ],
        defaultSegment: NO_SEGMENT_ID,
        exportTypes: [LEAD_LIST_EXPORT_KEY, REPORT_DATA_EXPORT_KEY, REPORT_CHART_EXPORT_KEY],
        primaryFilters: [
            'owner',
            'closedTime',
            'competitors',
            'lastContactedTime',
            'createdTime',
            'outcome',
            'products',
            'stage',
            'stageset',
            'sources',
            'tags',
            'value',
        ],
        tooltipUnit: 'leads',
        // @ts-expect-error
        filterFields: (fieldName) => !['status', 'closedTime'].includes(fieldName),
    },
    [NEW_LEADS]: {
        title: 'New leads',
        description: 'How many leads are we getting?',
        icon: ChevronRegularRightLeadReportIcon,
        documentTitle: 'New leads report · Nutshell',
        baseUrl: '/reports/newleads',
        defaultFields: DEFAULT_FIELDS_NEW_LEADS,
        defaultState: {
            search: `?report%5BdateRangeType%5D=createdTime&report%5Bgap%5D=w&report%5Bfacet%5D=total&filter%5BcreatedTime%5D=-d365&list%5Bfields%5D%5Bleads%5D=${encodeURIComponent(
                DEFAULT_FIELDS_NEW_LEADS
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
            dateRangeType: 'createdTime',
            dateRangeTypeFilter: LAST_365_DAYS.id,
        },
        dateRanges: DATE_RANGES_PAST,
        segmentOptions: [
            SEGMENT_NONE,
            SEGMENT_OUTCOME,
            SEGMENT_COMPETITOR,
            SEGMENT_PRODUCT,
            SEGMENT_PRODUCT_CATEGORY,
            SEGMENT_ASSIGNEE,
            SEGMENT_TERRITORY,
            SEGMENT_SOURCE,
            SEGMENT_TAG,
            SEGMENT_CHANNEL,
        ],
        defaultSegment: NO_SEGMENT_ID,
        exportTypes: [LEAD_LIST_EXPORT_KEY, REPORT_DATA_EXPORT_KEY, REPORT_CHART_EXPORT_KEY],
        primaryFilters: [
            'owner',
            'closedTime',
            'lastContactedTime',
            'createdTime',
            'outcome',
            'products',
            'stage',
            'stageset',
            'sources',
            'tags',
            'value',
        ],
        tooltipUnit: 'leads',
        // @ts-expect-error
        filterFields: (fieldName) => !['createdTime'].includes(fieldName),
    },
    [SALES]: {
        title: 'Sales',
        description: 'What are our sales this period?',
        icon: StarRegularReportIcon,
        documentTitle: 'Sales report · Nutshell',
        baseUrl: '/reports/sales',
        defaultFields: DEFAULT_FIELDS_SALES,
        defaultState: {
            search: `?report%5BdateRangeType%5D=closedTime&report%5Bgap%5D=w&report%5Bfacet%5D=total&filter%5BclosedTime%5D=-d365&filter%5Bstatus%5D=Won&list%5Bfields%5D%5Bleads%5D=${encodeURIComponent(
                DEFAULT_FIELDS_SALES
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
            dateRangeType: 'closedTime',
            dateRangeTypeFilter: LAST_365_DAYS.id,
        },
        dateRanges: DATE_RANGES_PAST,
        segmentOptions: [
            SEGMENT_NONE,
            SEGMENT_OUTCOME,
            SEGMENT_COMPETITOR,
            SEGMENT_PRODUCT,
            SEGMENT_PRODUCT_CATEGORY,
            SEGMENT_ASSIGNEE,
            SEGMENT_TERRITORY,
            SEGMENT_SOURCE,
            SEGMENT_TAG,
            SEGMENT_CHANNEL,
        ],
        defaultSegment: NO_SEGMENT_ID,
        exportTypes: [LEAD_LIST_EXPORT_KEY, REPORT_DATA_EXPORT_KEY, REPORT_CHART_EXPORT_KEY],
        primaryFilters: [
            'owner',
            'closedTime',
            'lastContactedTime',
            'createdTime',
            'products',
            'sources',
            'stageset',
            'tags',
            'value',
        ],
        tooltipUnit: 'leads',
        // @ts-expect-error
        filterFields: (fieldName) => !['status', 'closedTime'].includes(fieldName),
    },
    [PRODUCTS]: {
        title: 'Products',
        description: 'Which products are driving revenue?',
        icon: ProductReportIcon,
        documentTitle: 'Products report · Nutshell',
        baseUrl: '/reports/products',
        defaultFields: DEFAULT_FIELDS_SALES,
        defaultState: {
            // Defaults: Won leads closed in the last 365 days, sorted by revenue desc, 100/page.
            // Status and closedTime are baked into the default URL just like Sales.
            search: `?report%5BdateRangeType%5D=closedTime&filter%5BclosedTime%5D=-d365&filter%5Bstatus%5D=Won&list%5Bpage%5D=1&list%5Blimit%5D=100&list%5Bsort%5D=-revenue`,
            dateRangeType: 'closedTime',
            dateRangeTypeFilter: LAST_365_DAYS.id,
        },
        dateRanges: DATE_RANGES_PAST,
        // The Products report is a single-view aggregation, not a date-bucketed
        // chart with segmentation, so there are no chart-segment options.
        segmentOptions: [],
        defaultSegment: NO_SEGMENT_ID,
        exportTypes: [REPORT_PRODUCTS_EXPORT_KEY],
        // These are the secondary filters surfaced in the filter drawer. Product
        // and Status are promoted to top-of-page chips by the Products report
        // page itself, so we exclude them here.
        primaryFilters: [
            'owner',
            'closedTime',
            'createdTime',
            'lastContactedTime',
            'sources',
            'stageset',
            'tags',
            'value',
        ],
        tooltipUnit: 'products',
        // @ts-expect-error
        filterFields: (fieldName) => !['status', 'closedTime'].includes(fieldName),
    },
    [CUSTOM]: {
        title: 'Custom',
        description: 'What do we know across all leads?',
        icon: CogRegularReportIcon,
        documentTitle: 'Custom report · Nutshell',
        baseUrl: '/reports/custom',
        defaultFields: DEFAULT_FIELDS_CUSTOM,
        defaultState: {
            search: `?report%5BdateRangeType%5D=createdTime&report%5Bgap%5D=w&report%5Bfacet%5D=total&filter%5BcreatedTime%5D=-d365&list%5Bfields%5D%5Bleads%5D=${encodeURIComponent(
                DEFAULT_FIELDS_CUSTOM
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
            dateRangeType: 'createdTime',
            dateRangeTypeFilter: LAST_365_DAYS.id,
        },
        dateRanges: DATE_RANGES_PAST,
        segmentOptions: [
            SEGMENT_NONE,
            SEGMENT_OUTCOME,
            SEGMENT_PRODUCT,
            SEGMENT_PRODUCT_CATEGORY,
            SEGMENT_ASSIGNEE,
            SEGMENT_TERRITORY,
            SEGMENT_SOURCE,
            SEGMENT_TAG,
            SEGMENT_COMPETITOR,
            SEGMENT_STAGE,
            SEGMENT_STATUS,
            SEGMENT_CHANNEL,
        ],
        defaultSegment: NO_SEGMENT_ID,
        exportTypes: [LEAD_LIST_EXPORT_KEY, REPORT_DATA_EXPORT_KEY, REPORT_CHART_EXPORT_KEY],
        primaryFilters: [
            'closedTime',
            'createdTime',
            'lastContactedTime',
            'outcome',
            'owner',
            'products',
            'sources',
            'stage',
            'stageset',
            'status',
            'tags',
            'value',
        ],
        tooltipUnit: 'leads',
        filterFields: () => true, // This is controlled in components dynamicly based on dateRangeType
    },
    [FORECAST]: {
        title: 'Forecast',
        description: 'What are our expected sales?',
        icon: ChartLineRegularReportIcon,
        documentTitle: 'Forecast report · Nutshell',
        baseUrl: '/reports/forecast',
        defaultFields: DEFAULT_FIELDS_FORECAST,
        defaultState: {
            search: `?report%5BdateRangeType%5D=closedTime&report%5Bgap%5D=w&report%5Bfacet%5D=total&report%5BuseConfidence%5D=false&filter%5Bstatus%5D=Won%2COpen&filter%5BclosedTime%5D=%2Bd90&list%5Bfields%5D%5Bleads%5D=${encodeURIComponent(
                DEFAULT_FIELDS_FORECAST
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
            dateRangeType: 'closedTime',
            dateRangeTypeFilter: '+d90',
        },
        dateRanges: DATE_RANGES_FUTURE,
        segmentOptions: [
            SEGMENT_NONE,
            SEGMENT_PRODUCT,
            SEGMENT_PRODUCT_CATEGORY,
            SEGMENT_ASSIGNEE,
            SEGMENT_SOURCE,
            SEGMENT_TAG,
            SEGMENT_COMPETITOR,
            SEGMENT_STAGE,
            SEGMENT_TERRITORY,
            SEGMENT_CHANNEL,
        ],
        defaultSegment: NO_SEGMENT_ID,
        exportTypes: [LEAD_LIST_EXPORT_KEY, REPORT_DATA_EXPORT_KEY, REPORT_CHART_EXPORT_KEY],
        primaryFilters: [
            'owner',
            'closedTime',
            'lastContactedTime',
            'createdTime',
            'outcome',
            'products',
            'stageset',
            'tags',
            'value',
        ],
        tooltipUnit: 'leads',
        // @ts-expect-error
        filterFields: (fieldName) => !['status', 'closedTime'].includes(fieldName),
    },
    [ATTRIBUTION]: {
        title: 'Attribution',
        description: getAttributionReportDescription(),
        icon: TagRegularDiagonalReportIcon,
        documentTitle: 'Attribution report · Nutshell',
        baseUrl: '/reports/attribution',
        defaultFields: DEFAULT_FIELDS_ATTRIBUTION,
        defaultState: {
            search: `?report%5BdateRangeType%5D=period&report%5Bgap%5D=w&report%5Bfacet%5D=total&report%5BuseConfidence%5D=false&filter%5Bperiod%5D=-d90&list%5Bfields%5D%5Bleads%5D=${encodeURIComponent(
                DEFAULT_FIELDS_ATTRIBUTION
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
            dateRangeType: 'period',
            dateRangeTypeFilter: '-d90',
        },
        dateRanges: DATE_RANGES_ATTRIBUTION,
        segmentOptions: [],
        defaultSegment: NO_SEGMENT_ID,
        exportTypes: [],
        primaryFilters: [
            'owner',
            'lastContactedTime',
            'createdTime',
            'outcome',
            'products',
            'stageset',
            'tags',
            'value',
        ],
        tooltipUnit: 'leads',
        // @ts-expect-error
        filterFields: (fieldName) =>
            !['status', 'period', 'createdTime', 'closedTime'].includes(fieldName),
    },
    [ENGAGEMENT_LEADS]: {
        title: 'Engagement leads',
        alwaysShowTitle: true,
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '/reports/engagement-leads',
        defaultState: {
            search: '?report%5BdateRangeType%5D=period&report%5Bgap%5D=w&filter%5Bperiod%5D=-d30',
            dateRangeType: 'period',
            dateRangeTypeFilter: LAST_365_DAYS.id,
            facet: 'count',
        },
        tooltipUnit: 'leads',
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: SEGMENT_NONE,
        exportTypes: [],
        primaryFilters: [],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [ENGAGEMENT_SALES]: {
        title: 'Engagement sales',
        alwaysShowTitle: true,
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '/reports/engagement-sales',
        defaultState: {
            search: '?report%5BdateRangeType%5D=period&report%5Bgap%5D=w&filter%5Bperiod%5D=-d30',
            dateRangeType: 'period',
            dateRangeTypeFilter: LAST_365_DAYS.id,
            facet: 'total',
        },
        tooltipUnit: null,
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: SEGMENT_NONE,
        exportTypes: [],
        primaryFilters: [],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
} as const;

const funnelReportConstants = {
    [FUNNEL]: {
        title: 'Funnel',
        description: 'What are our conversion rates?',
        icon: FilterRegularReportIcon,
        documentTitle: 'Funnel report · Nutshell',
        baseUrl: '/reports/funnel',
        dateRangeType: 'closedTime',
        defaultState: {
            // This is a bit hacky, but the server handles the default search
            // params for funnel report
            search: '',
        },
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: undefined,
        exportTypes: [REPORT_FUNNEL_EXPORT_KEY],
        tooltipUnit: 'leads',
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
} as const;

const activityReportConstants = {
    [ACTIVITY]: {
        title: 'Activity',
        description: 'How are we performing against our goals?',
        icon: CalendarRegularReportIcon,
        documentTitle: 'Activity report · Nutshell',
        baseUrl: '/reports/activity',
        defaultState: {
            search: '?filter[period]=-d90&report%5Bgap%5D=w&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&report%5Bsegment%5D=activityType&filter%5Bstatus%5D=logged',
            report: {
                gap: 'w',
                dateRangeType: 'period',
                facet: 'count',
            },
            sort: {
                by: 'User',
                direction: 'des',
            },
        },
        tooltipUnit: null,
        dateRanges: DATE_RANGES_PAST,
        segmentOptions: [SEGMENT_NONE, SEGMENT_USER, SEGMENT_ACTIVITY_TYPE, SEGMENT_LEAD_STATUS],
        defaultSegment: SEGMENT_ACTIVITY_TYPE,
        exportTypes: [REPORT_ACTIVITY_DETAILS_EXPORT_KEY, REPORT_ACTIVITY_OVERVIEW_EXPORT_KEY],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
} as const;

const emailReportConstants = {
    [EMAIL]: {
        title: 'Email',
        description: 'What kind of emails are we sending?',
        icon: EnvelopeRegularReportIcon,
        documentTitle: 'Email report · Nutshell',
        baseUrl: '/reports/email',
        defaultState: {
            search: '?filter[period]=-d90&report%5Bgap%5D=w&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&report%5Bsegment%5D=emailSequenceTemplateGroups',
            report: {
                gap: 'w',
                dateRangeType: 'period',
                facet: 'count',
            },
            sort: {
                by: 'User',
                direction: 'des',
            },
        },
        tooltipUnit: null,
        dateRanges: DATE_RANGES_PAST,
        segmentOptions: [SEGMENT_NONE, SEGMENT_USER, SEGMENT_EMAIL_SEQUENCE_TEMPLATE_GROUP],
        defaultSegment: SEGMENT_EMAIL_SEQUENCE_TEMPLATE_GROUP,
        exportTypes: [],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
} as const;

const tasksReportConstants = {
    [TASKS]: {
        title: 'Tasks',
        description: 'What are our task metrics?',
        icon: CheckRegularReportIcon,
        documentTitle: 'Tasks report · Nutshell',
        baseUrl: '/reports/tasks',
        defaultState: {
            search: '?filter[period]=-d90&report%5Bgap%5D=w&report%5Bfacet%5D=count&report%5Bsegment%5D=taskStatuses',
            report: {
                gap: 'w',
                dateRangeType: 'period',
                facet: 'count',
            },
            sort: {
                by: 'User',
                direction: 'des',
            },
        },
        tooltipUnit: null,
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: SEGMENT_TASK_STATUS,
        exportTypes: [REPORT_DATA_EXPORT_KEY, REPORT_CHART_EXPORT_KEY],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
} as const;

const chatbotReportConstants = {
    [CHATBOT]: {
        title: 'Chatbot',
        description: 'How are users interacting with your chatbot?',
        icon: ChatbotRegularReportIcon,
        documentTitle: 'Chatbot report · Nutshell',
        baseUrl: '/reports/chatbot',
        defaultState: {
            search:
                '?report%5BdateRangeType%5D=period&filter%5Bperiod%5D=' +
                DEFAULT_CHATBOT_REPORT_PERIOD,
        },
        tooltipUnit: null,
        dateRanges: DATE_RANGES_PAST_WITHOUT_TODAY,
        exportTypes: [],
        defaultSegment: undefined,
        shouldHydrateFromLocalStorage: false,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
        maxDate: moment().subtract(1, 'day'),
    },
} as const;

const schedulerReportConstants = {
    [SCHEDULER_REPORT]: {
        title: 'Scheduler',
        description: 'Booking stats and trends for your meeting in the past 3 months',
        icon: CalendarPenRegularReportIcon,
        documentTitle: 'Scheduler report · Nutshell',
        baseUrl: '/reports/schedulerReport',
        defaultState: {
            search: '',
        },
        tooltipUnit: null,
        dateRanges: DATE_RANGES_PAST,
        exportTypes: [],
        defaultSegment: undefined,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
} as const;

const engagementReportConstants = {
    [ENGAGEMENT]: {
        title: 'Engagement',
        description: 'How are users interacting with your chat widget?',
        icon: ChatRegularReportIcon,
        documentTitle: 'Engagement report · Nutshell',
        baseUrl: 'reports/engagement',
        defaultState: {
            search: '?filter%5BcreatedTime%5D=-d31&report%5Bgap%5D=d&report%5BdateRangeType%5D=createdTime',
            report: {},
            sort: {},
        },
        tooltipUnit: null,
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: SEGMENT_NONE,
        exportTypes: [],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
        // We do not have data before this date so we need to restrict
        // the date range picker
        minDate: moment('2025-12-01 00:00:00'),
        maxDate: moment(),
    },
    [WIDGET_OPENS]: {
        title: 'Widget opens',
        alwaysShowTitle: true,
        description: 'How many times was the chat widget opened?',
        icon: ChatRegularReportIcon,
        documentTitle: 'Widget opens · Nutshell',
        baseUrl: 'reports/engagement',
        defaultState: {
            search: '?filter%5BcreatedTime%5D=-d30&report%5Bgap%5D=w&report%5BdateRangeType%5D=createdTime',
            report: {},
            sort: {},
        },
        tooltipUnit: 'opens',
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: SEGMENT_NONE,
        exportTypes: [],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [COMPLETED_ACTIONS]: {
        title: 'Completed actions',
        alwaysShowTitle: true,
        description: 'What actions were completed through the chat widget?',
        icon: ChatRegularReportIcon,
        documentTitle: 'Completed actions · Nutshell',
        baseUrl: 'reports/engagement',
        defaultState: {
            search: '?filter%5BcreatedTime%5D=-d30&report%5Bgap%5D=w&report%5BdateRangeType%5D=createdTime',
            report: {},
            sort: {},
        },
        tooltipUnit: 'actions',
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: SEGMENT_ACTION_TYPE.id,
        exportTypes: [],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [VISITORS]: {
        title: 'Visitors',
        alwaysShowTitle: true,
        description: 'How many visitors did you get?',
        icon: ChatRegularReportIcon,
        documentTitle: 'Visitors · Nutshell',
        baseUrl: 'reports/engagement',
        defaultState: {
            search: '?filter%5BcreatedTime%5D=-d30&report%5Bgap%5D=w&report%5BdateRangeType%5D=createdTime',
            report: {},
            sort: {},
        },
        tooltipUnit: 'visitors',
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: SEGMENT_NONE,
        exportTypes: [],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [ENGAGEMENT_BAR_LEADS]: {
        title: 'Engagement bar leads',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: DEFAULT_FIELDS_ENGAGEMENT_BAR_LEADS,
        dateRangeType: 'period',
        defaultState: {
            search: `?list%5Bfields%5D%5Bleads%5D=${encodeURIComponent(
                DEFAULT_FIELDS_ENGAGEMENT_BAR_LEADS
            )}&list%5Bpage%5D=1&list%5Bsort%5D=${DEFAULT_SORT_FIELD_ENGAGEMENT_BAR_LEADS}`,
        },
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
} as const;

const quotesReportConstants = {
    [QUOTES]: {
        title: 'Quotes',
        description: 'What are our quote metrics?',
        icon: CogRegularReportIcon,
        documentTitle: 'Quotes report · Nutshell',
        baseUrl: '/reports/quotes',
        defaultState: {
            search: '?filter%5Bperiod%5D=-d30',
            report: {},
            sort: {},
        },
        tooltipUnit: null,
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: SEGMENT_QUOTE_STATUS,
        exportTypes: [],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
} as const;

const invoicesReportConstants = {
    [INVOICES]: {
        title: 'Invoices',
        description: 'What are our invoice metrics?',
        documentTitle: 'Invoices report · Nutshell',
        baseUrl: '/reports/invoices',
        icon: CogRegularReportIcon,
        defaultState: {
            search: '?filter%5Bperiod%5D=-d30',
            report: {},
            sort: {},
        },
        tooltipUnit: null,
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: SEGMENT_INVOICE_STATUS,
        exportTypes: [],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
} as const;

const installmentsReportConstants = {
    [INSTALLMENTS]: {
        title: 'Installments',
        description: 'What installments are due and collected this period?',
        documentTitle: 'Installments · Nutshell',
        baseUrl: '/reports/installments',
        icon: TagRegularDiagonalReportIcon,
        defaultState: {
            search: `?filter%5Bperiod%5D=fy&report%5BdateRangeType%5D=period&report%5Bgap%5D=m&report%5Bfacet%5D=total&includeOpenLeads=true`,
        },
        tooltipUnit: null,
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: undefined,
        exportTypes: [],
        shouldHydrateFromLocalStorage: false,
        primaryFilters: [
            'owner',
            'closedTime',
            'lastContactedTime',
            'sources',
            'stageset',
            'tags',
            'value',
        ],
        // @ts-expect-error
        filterFields: (fieldName) => fieldName !== 'period' && fieldName !== 'status',
    },
} as const;

// TODO: Probably get these out of here
const marketingReportConstants = {
    [DASHBOARD]: {
        title: 'Dashboard',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        dateRangeType: 'period',
        defaultState: {
            search: '?filter%5Bperiod%5D=-d30',
        },
        dateRanges: DATE_RANGES_DASHBOARD,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [ENGAGEMENT_DASHBOARD]: {
        title: 'Dashboard',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        dateRangeType: 'period',
        defaultState: {
            search: '?filter%5Bperiod%5D=-d30',
        },
        dateRanges: DATE_RANGES_ENGAGEMENT_DASHBOARD,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [AUDIENCE_MEMBERSHIP]: {
        title: 'Audience membership',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: DEFAULT_FIELDS_AUDIENCE_MEMBERSHIP,
        dateRangeType: 'period',
        defaultState: {
            search: `?filter%5Bperiod%5D=-d90&filter%5BincludeExisting%5D=0&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&report%5BreportType%5D=membership&report%5Bgap%5D=w&report%5Bsegment%5D=subscriptionStatus&list%5Bfields%5D%5Bcontacts%5D=${encodeURIComponent(
                DEFAULT_FIELDS_AUDIENCE_MEMBERSHIP
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
        },
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [EDITION_ENGAGEMENT]: {
        title: 'Edition engagement',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: DEFAULT_FIELDS_EDITION_ENGAGEMENT,
        dateRangeType: 'period',
        defaultState: {
            search: `?filter%5Bperiod%5D=%2bh4&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&&report%5Bgap%5D=i&report%5Bsegment%5D=interactionType&list%5Bfields%5D%5Bcontacts%5D=${encodeURIComponent(
                DEFAULT_FIELDS_EDITION_ENGAGEMENT
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
        },
        dateRanges: DATE_RANGES_MINUTES,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [SMS_EDITION_ENGAGEMENT]: {
        title: 'SMS edition engagement',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: DEFAULT_FIELDS_SMS_EDITION_ENGAGEMENT,
        dateRangeType: 'period',
        defaultState: {
            search: `?filter%5Bperiod%5D=%2bh4&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&&report%5Bgap%5D=i&report%5Bsegment%5D=interactionType&list%5Bfields%5D%5Bcontacts%5D=${encodeURIComponent(
                DEFAULT_FIELDS_SMS_EDITION_ENGAGEMENT
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
        },
        dateRanges: DATE_RANGES_MINUTES,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [DRIP_EDITION_ENGAGEMENT]: {
        title: 'Drip edition engagement',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: DEFAULT_FIELDS_DRIP_EDITION_ENGAGEMENT,
        dateRangeType: 'period',
        defaultState: {
            search: `?filter%5Bperiod%5D=-d30&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&&report%5Bgap%5D=d&report%5Bsegment%5D=interactionType&list%5Bfields%5D%5Bcontacts%5D=${encodeURIComponent(
                DEFAULT_FIELDS_DRIP_EDITION_ENGAGEMENT
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
        },
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [SMS_DRIP_EDITION_ENGAGEMENT]: {
        title: 'SMS drip edition engagement',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: DEFAULT_FIELDS_SMS_DRIP_EDITION_ENGAGEMENT,
        dateRangeType: 'period',
        defaultState: {
            search: `?filter%5Bperiod%5D=-d30&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&&report%5Bgap%5D=d&report%5Bsegment%5D=interactionType&list%5Bfields%5D%5Bcontacts%5D=${encodeURIComponent(
                DEFAULT_FIELDS_SMS_DRIP_EDITION_ENGAGEMENT
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
        },
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [FORM_ENGAGEMENT]: {
        title: 'Form engagement',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: DEFAULT_FIELDS_FORM_ENGAGEMENT,
        dateRangeType: 'period',
        defaultState: {
            search: `?filter%5Bperiod%5D=-d30&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&&report%5Bgap%5D=d&report%5Bsegment%5D=interactionType&list%5Bfields%5D%5Bcontacts%5D=${encodeURIComponent(
                DEFAULT_FIELDS_FORM_ENGAGEMENT
            )}&list%5Bpage%5D=1&list%5Bsort%5D=name`,
        },
        dateRanges: DATE_RANGES_FORMS,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [SITE_ANALYTICS]: {
        title: 'Site analytics',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: null,
        dateRangeType: 'period',
        defaultState: {
            search: `?filter%5Bperiod%5D=-d30&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&&report%5Bgap%5D=d&report%5Bsegment%5D=interactionType`,
        },
        dateRanges: DATE_RANGES_ANALYTICS,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [MCFXCOMPANIES]: {
        title: 'Visitors',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: DEFAULT_FIELDS_MCFXCOMPANIES,
        dateRangeType: 'period',
        defaultState: {
            search: `?filter%5Bperiod%5D=-d30&list%5Bfields%5D%mcfxcompanies%5D=${encodeURIComponent(
                DEFAULT_FIELDS_MCFXCOMPANIES
            )}&list%5Bsort%5D=-createdTime&list%5Bpage%5D=1`,
        },
        dateRanges: DATE_RANGES_FORMS,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [SCHEDULER]: {
        title: 'Scheduler',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: null,
        dateRangeType: 'period',
        defaultState: {
            search: '?filter%5Bperiod%5D=-d30&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&&report%5Bgap%5D=d&report%5Bsegment%5D=interactionType',
        },
        dateRanges: DATE_RANGES_PAST,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [COMPETITORS]: {
        title: 'CompetitorSpy',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: null,
        dateRangeType: 'period',
        defaultState: {
            search: '',
        },
        dateRanges: DATE_RANGES_ANALYTICS,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
    [LANDING_PAGES]: {
        title: 'Landing pages',
        description: '',
        documentTitle: '',
        icon: CogRegularReportIcon,
        baseUrl: '',
        defaultFields: null,
        dateRangeType: 'period',
        defaultState: {
            search: `?filter%5Bperiod%5D=-d30&report%5BdateRangeType%5D=period&report%5Bfacet%5D=count&&report%5Bgap%5D=d&report%5Bsegment%5D=interactionType`,
        },
        dateRanges: DATE_RANGES_ANALYTICS,
        defaultSegment: undefined,
        exportTypes: [],
        tooltipUnit: null,
        // @ts-expect-error
        filterFields: (fieldName) => fieldName,
    },
} as const;

const reportConstants = {
    ...leadsReportConstants,
    ...funnelReportConstants,
    ...activityReportConstants,
    ...emailReportConstants,
    ...marketingReportConstants,
    ...tasksReportConstants,
    ...engagementReportConstants,
    ...quotesReportConstants,
    ...invoicesReportConstants,
    ...installmentsReportConstants,
    ...chatbotReportConstants,
    ...schedulerReportConstants,
} as const;

export function getReportTitle(reportKey: ReportKey): string {
    return reportConstants[reportKey].title || 'Report';
}

export function getReportTitleIsAlwaysShown(reportKey: ReportKey): boolean {
    // @ts-expect-error
    return reportConstants[reportKey].alwaysShowTitle || false;
}

export function getReportDescription(reportKey: ReportKey): string | React.ReactNode {
    return reportConstants[reportKey].description || '';
}

export function getReportFieldsFilterFunction(reportKey: ReportKey): (arg1: string) => boolean {
    return reportConstants[reportKey].filterFields;
}

export function getReportDocumentTitle(reportKey: ReportKey) {
    return reportConstants[reportKey].documentTitle;
}

export function getReportBaseUrl(reportKey: ReportKey) {
    return reportConstants[reportKey].baseUrl;
}

export function getReportDefaultSegment(reportKey: ReportKey) {
    return reportConstants[reportKey].defaultSegment;
}

export function getReportDefaultState(reportKey: ReportKey) {
    return reportConstants[reportKey].defaultState;
}

export function getPrimaryLeadsReportFilters(reportKey: ReportKey): string[] {
    // @ts-expect-error — merged report config; not every key has primaryFilters in typings
    const primaryFilters = reportConstants[reportKey].primaryFilters;
    return Array.isArray(primaryFilters) ? primaryFilters : [];
}

export function getShouldAllowSegmentation(reportKey: ReportKey) {
    return Boolean(getReportSegments(reportKey).length);
}

// @ts-expect-error
export function getReportMinDate(reportKey: ReportKey): Moment | null | undefined {
    // @ts-expect-error
    return reportConstants[reportKey].minDate || undefined;
}

// @ts-expect-error
export function getReportMaxDate(reportKey: ReportKey): Moment | null | undefined {
    // @ts-expect-error
    return reportConstants[reportKey].maxDate || undefined;
}

export function isMarketingReportKey(reportKey: ReportKey) {
    // @ts-expect-error
    return typeof marketingReportConstants[reportKey] !== 'undefined';
}
export function getReportSegmentDisplayType(reportKey: ReportKey) {
    return reportKey === EDITION_ENGAGEMENT ? 'line' : 'bar';
}

/**
 * Returns the name that the `/rest/report` api uses for the primary report for a given
 * reportKey.
 *
 * @param  {string} reportKey The identifier for the report type
 * @return {string}           Primary report name
 */
export function getApiReportName(reportKey: LeadsReportKey | FunnelReportKey): string {
    if (reportKey === FORECAST) {
        return 'Sales';
    }

    return 'Leads';
}

export function getReportKeyFromReportSavedType(reportSavedType: ReportSavedTypeKey): ReportKey {
    switch (reportSavedType) {
        case LOSSES_SAVED_TYPE:
            return LOSSES;
        case NEW_LEADS_SAVED_TYPE:
            return NEW_LEADS;
        case SALES_SAVED_TYPE:
            return SALES;
        case PRODUCTS_SAVED_TYPE:
            return PRODUCTS;
        case FORECAST_SAVED_TYPE:
            return FORECAST;
        case FUNNEL_SAVED_TYPE:
            return FUNNEL;
        case CUSTOM_SAVED_TYPE:
            return CUSTOM;
        case ACTIVITY_SAVED_TYPE:
            return ACTIVITY;
        case EMAIL_SAVED_TYPE:
            return EMAIL;
        case TASKS_SAVED_TYPE:
            return TASKS;
        case ENGAGEMENT_SAVED_TYPE:
            return ENGAGEMENT;
        default:
            return CUSTOM;
    }
}

export function getReportSaveType(reportKey: ReportKey): ReportSavedTypeKey {
    switch (reportKey) {
        case LOSSES:
            return LOSSES_SAVED_TYPE;
        case NEW_LEADS:
            return NEW_LEADS_SAVED_TYPE;
        case SALES:
            return SALES_SAVED_TYPE;
        case PRODUCTS:
            return PRODUCTS_SAVED_TYPE;
        case FORECAST:
            return FORECAST_SAVED_TYPE;
        case ATTRIBUTION:
            return ATTRIBUTION_SAVED_TYPE;
        case FUNNEL:
            return FUNNEL_SAVED_TYPE;
        case CUSTOM:
            return CUSTOM_SAVED_TYPE;
        case ACTIVITY:
            return ACTIVITY_SAVED_TYPE;
        case EMAIL:
            return EMAIL_SAVED_TYPE;
        case TASKS:
            return TASKS_SAVED_TYPE;
        case ENGAGEMENT:
            return ENGAGEMENT_SAVED_TYPE;
        default:
            return CUSTOM_SAVED_TYPE;
    }
}

export function getExportTypes(reportKey: ReportKey): Array<ExportKey> {
    // @ts-expect-error
    return reportConstants[reportKey].exportTypes;
}

export function getReportSegments(reportKey: ReportKey): Array<SegmentOption> {
    // @ts-expect-error
    return reportConstants[reportKey].segmentOptions || [];
}

export function getReportDefaultFields(reportKey: ReportKey): string {
    // @ts-expect-error
    return reportConstants[reportKey].defaultFields || '';
}

export function getReportIcon(reportKey: ReportKey): React.ComponentType<any> {
    return reportConstants[reportKey].icon;
}

export function getTooltipUnit(reportKey: ReportKey) {
    return reportConstants[reportKey].tooltipUnit;
}

export function getReportDateRanges(reportKey: ReportKey) {
    return reportConstants[reportKey].dateRanges;
}

export function getShouldHydrateFromLocalStorage(reportKey: ReportKey): boolean {
    const reportConfig = reportConstants[reportKey] as {
        shouldHydrateFromLocalStorage?: boolean;
    };

    return reportConfig.shouldHydrateFromLocalStorage ?? true;
}

const REPORT_KEYS_WITH_SINGLE_METRIC_TOOLTIP: ReportKey[] = [INSTALLMENTS];

export function getTooltipSingleMetricColumn(
    reportKey: ReportKey,
    reportFacet?: ReportFacet
): ReportFacet | null {
    if (REPORT_KEYS_WITH_SINGLE_METRIC_TOOLTIP.includes(reportKey) && reportFacet) {
        return reportFacet;
    }

    return null;
}

const reportTypeEnumToReportKey: Partial<Record<SavedFiltersReportType, ReportKey>> = {
    REPORT_SALES: 'sales',
    REPORT_LOSSES: 'losses',
    REPORT_FORECAST: 'forecast',
    REPORT_FUNNEL: 'funnel',
    REPORT_NEWLEADS: 'newleads',
    REPORT_CUSTOM: 'custom',
    REPORT_ACTIVITY: 'activity',
    REPORT_EMAIL: 'email',
    REPORT_ATTRIBUTION: 'attribution',
    REPORT_TASKS: 'tasks',
    REPORT_PRODUCTS: 'products',
};

export function convertReportTypeGraphQlEnumToReportKey(
    reportType: SavedFiltersReportType
): ReportKey {
    // @ts-expect-error
    return reportTypeEnumToReportKey[reportType];
}
